import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { CardComponent, Collapse } from 'components/UI';
import { vehicleActions } from 'state/modules/vehicles';
import { getPowertrains, toTitleCase } from 'utils/utils';
import { powertrainOptionalValues } from 'constants/vehicleTypes';
import { Box, Typography, Grid, Link, List, ListItem, ListItemText } from '@mui/material';

const collapseCardClasses = {
  root: {
    marginTop: '20px !important',
    marginBottom: '20px !important',
    '& button': {
      borderRadius: '4px 4px 0 0',
    },
  },
  box: {
    marginTop: 0,
    borderTop: 0,
    borderRadius: '0 0 4px 4px',
  },
  buttonClass: {
    backgroundColor: '#FBFBFB',
    border: '1px solid #EEEEEE',
    boxShadow: 'none',
    color: '#4D4F5C',
    padding: '6px 24px',
    textTransform: 'capitalize',
    fontSize: '13px',
    '&:hover': {
      backgroundColor: '#FBFBFB',
      border: '1px solid #EEEEEE',
      boxShadow: 'none',
      color: '#4D4F5C',
    },
  },
};

export const VehicleTypeDetailsWidget = ({ vehicleType = {} }) => {
  const { id } = vehicleType;
  const history = useHistory();
  const dispatch = useDispatch();

  const fetchingUploadImage = useSelector((state) => state.files?.fetchingUploadImage);
  const fetchedUploadImageSuccess = useSelector((state) => state.files?.fetchedUploadImageSuccess);
  const fetchingDeleteImage = useSelector((state) => state.files?.fetchingDeleteImage);
  const fetchedDeleteImageSuccess = useSelector((state) => state.files?.fetchedDeleteImageSuccess);

  const reload =
    (id && !fetchingUploadImage && fetchedUploadImageSuccess) ||
    (id && !fetchingDeleteImage && fetchedDeleteImageSuccess);

  useEffect(() => {
    if (reload) {
      dispatch(vehicleActions.fetchVehicleTypeById(id));
    }
  }, [id, reload, dispatch]);

  const goToVehicleTypeEdition = (e) => {
    e.preventDefault();
    history.push(buildUrl(`/vehicle-types/${id}/update`));
  };

  const findPowertrain = (value) => {
    const powertrain = getPowertrains()?.find((pt) => pt.value === value);
    return powertrain?.label || '';
  };

  const getVehicleTypeData = (vehicleType = {}) => {
    const vehicletypeInfo = [
      {
        title: i18n.t('vehicle_types.quick_specs'),
        items: [
          {
            label: `${i18n.t('vehicle_types.year')}:`,
            value: vehicleType?.year || '',
          },
          {
            label: `${i18n.t('vehicle_types.make')}:`,
            value: vehicleType?.make || '-',
          },
          {
            label: `${i18n.t('vehicle_types.model')}:`,
            value: vehicleType?.model || '-',
          },
          {
            label: `${i18n.t('vehicle_types.trim')}:`,
            value: vehicleType?.trim || '-',
          },
          {
            label: `${i18n.t('vehicle_types.category')}:`,
            value: toTitleCase(vehicleType?.category) || '-',
          },
        ],
      },
      {
        title: i18n.t('vehicle_types.performance_specs'),
        items: [
          {
            label: `${i18n.t('vehicle_types.powertrain')}:`,
            value: findPowertrain(vehicleType?.powertrain),
          },
          {
            label: `${i18n.t('vehicle_types.asset_class')}:`,
            value: vehicleType?.asset_class || '-',
          },
          {
            label: `${i18n.t('vehicle_types.msrp')}:`,
            value: vehicleType?.msrp || '-',
          },
          {
            label: `${i18n.t('vehicle_types.power')}:`,
            value: vehicleType?.power
              ? `${vehicleType?.power?.magnitude} ${vehicleType?.power?.units}`
              : '-',
          },
          {
            label: `${i18n.t('vehicle_types.range')}:`,
            value: vehicleType?.range
              ? `${vehicleType?.range?.magnitude} ${vehicleType?.range?.units}`
              : '-',
          },
        ],
      },
      {
        title: i18n.t('vehicle_types.engine_specs'),
        hide: !powertrainOptionalValues.includes(vehicleType?.powertrain),
        items: [
          {
            label: `${i18n.t('vehicle_types.fuel_capacity')}:`,
            value: vehicleType?.fuel_capacity
              ? `${vehicleType?.fuel_capacity?.magnitude} ${vehicleType?.fuel_capacity?.units}`
              : '-',
          },
          {
            label: `${i18n.t('vehicle_types.displacement')}:`,
            value: vehicleType?.displacement
              ? `${vehicleType?.displacement?.magnitude} ${vehicleType?.displacement?.units}`
              : '-',
          },
          {
            label: `${i18n.t('vehicle_types.cylinders')}:`,
            value: vehicleType?.cylinders || '-',
          },
          {
            label: `${i18n.t('vehicle_types.engine_type')}:`,
            value: vehicleType?.engine_type || '-',
          },
        ],
      },
    ];

    return vehicletypeInfo;
  };

  const getVehicleTypeInfo = (vehicleType = {}) => {
    const vehicletypeInfo = [{ label: 'Description:', value: vehicleType?.description || '' }];

    return vehicletypeInfo;
  };

  const Title = () => {
    return vehicleType?.year && vehicleType?.make && vehicleType?.model && vehicleType?.powertrain
      ? `${vehicleType?.year} ${vehicleType?.make} ${vehicleType?.model} ${vehicleType?.powertrain}`
      : '';
  };

  return (
    <CardComponent
      title="Vehicle Type Details"
      headerActionType="vertical"
      contentRootClasses={{ padding: 0 }}
    >
      <Box p="24px">
        <Grid
          container
          rowSpacing={1}
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          columnSpacing={{ xs: 1 }}
        >
          <Grid>
            <Typography variant="h3">
              <Box sx={{ fontWeight: 500, fontSize: '16px', color: 'text.primary' }}>ID# {id}</Box>
            </Typography>
          </Grid>
          <Grid sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Link
              href="#"
              onClick={goToVehicleTypeEdition}
              sx={{
                textDecoration: 'none !important',
                '&:hover': { textDecoration: 'none', fontWeight: 500 },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'primary.main',
                  textDecoration: 'none',
                }}
              >
                {i18n.t('vehicle.update_info')}
              </Typography>
            </Link>
          </Grid>
          {vehicleType?.default_file?.thumbnail_url && (
            <Grid size={12}>
              <Box maxHeight={150}>
                <img alt={vehicleType.name} src={vehicleType.default_file?.thumbnail_url} />
              </Box>
            </Grid>
          )}
          <Grid size={8}>
            <Typography variant="h3">
              <Box sx={{ fontSize: 16, fontWeight: 500, color: 'text.primary' }}>
                <Title />
              </Box>
            </Typography>
          </Grid>
        </Grid>

        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid size={12}>
            <List>
              {getVehicleTypeInfo(vehicleType).map((val, index) => (
                <ListItem
                  key={`info-${index}`}
                  sx={{
                    width: '100%',
                    padding: '0 4px 8px 0',
                    '&:not(:last-of-type)': {
                      borderBottom: (theme) => `1px solid ${theme.palette.common.greyLight}`,
                    },
                  }}
                >
                  <ListItemText
                    primary={val?.label}
                    slotProps={{
                      primary: {
                        variant: 'h6',
                        sx: {
                          color: 'secondary.dark',
                          fontWeight: 'bold',
                          fontSize: '13px',
                          wordBreak: 'break-word',
                        },
                      },
                    }}
                    secondary={
                      <Box display="flex" component="span" flexDirection="column">
                        <Typography variant="body2" component="span" color="textSecondary">
                          {val?.value}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>

        <Box>
          {getVehicleTypeData(vehicleType).map((val, index) => (
            <Box key={index}>
              {!val?.hide && (
                <Collapse key={index} title={val.title} classes={collapseCardClasses}>
                  <List disablePadding>
                    {val.items.map((val, index) => (
                      <ListItem
                        key={index}
                        disableGutters
                        disablePadding
                        sx={{
                          padding: 0,
                          width: '100%',
                          '&:not(:last-of-type)': {
                            borderBottom: (theme) => `1px solid ${theme.palette.common.greyLight}`,
                          },
                        }}
                      >
                        <ListItemText
                          primary={val.label}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                          slotProps={{
                            primary: {
                              variant: 'h6',
                              sx: {
                                color: 'secondary.dark',
                                fontWeight: 'bold',
                                fontSize: '13px',
                                wordBreak: 'break-word',
                                display: 'inline-block',
                              },
                            },
                            secondary: {
                              variant: 'body2',
                              display: 'inline-block',
                            },
                          }}
                          secondary={
                            <Box
                              display="flex"
                              component="span"
                              textAlign="right"
                              flexDirection="column"
                            >
                              <Typography variant="body2" component="span" color="textSecondary">
                                {val.value}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </CardComponent>
  );
};

VehicleTypeDetailsWidget.propTypes = {
  vehicleType: PropTypes.object,
};
